import { Component, EventEmitter, Input, Output } from '@angular/core';

import { AiConversation } from '../../../core/services/ai-agents';
import { AiAgentChatStateService } from '../../../core/services/ai-agent-chat-state';

@Component({
  selector: 'app-ai-conversation-list',
  standalone: false,
  templateUrl: './ai-conversation-list.html',
  styleUrl: './ai-conversation-list.scss',
})
export class AiConversationList {
  @Input() compact = false;
  @Input() collapsed = false;
  @Output() conversationSelected = new EventEmitter<string>();
  @Output() newConversation = new EventEmitter<void>();
  openMenuId: string | null = null;
  dialog: 'create-project' | 'rename-chat' | 'delete-chat' | null = null;
  dialogConversation: AiConversation | null = null;
  dialogValue = '';
  dialogError = '';
  dialogSaving = false;

  constructor(public readonly state: AiAgentChatStateService) {}

  select(id: string): void { this.openMenuId = null; this.conversationSelected.emit(id); }
  start(): void { this.newConversation.emit(); }
  toggleMenu(event: MouseEvent, id: string): void { event.stopPropagation(); this.openMenuId = this.openMenuId === id ? null : id; }

  rename(event: MouseEvent, conversation: AiConversation): void {
    event.stopPropagation();
    this.openMenuId = null;
    this.dialogConversation = conversation;
    this.dialogValue = conversation.title;
    this.dialogError = '';
    this.dialog = 'rename-chat';
  }

  async pin(event: MouseEvent, conversation: AiConversation): Promise<void> {
    event.stopPropagation();
    await this.state.togglePinned(conversation);
    this.openMenuId = null;
  }

  async move(event: MouseEvent, conversation: AiConversation, groupId: string | null): Promise<void> {
    event.stopPropagation();
    await this.state.moveToGroup(conversation, groupId);
    this.openMenuId = null;
  }

  remove(event: MouseEvent, conversation: AiConversation): void {
    event.stopPropagation();
    this.openMenuId = null;
    this.dialogConversation = conversation;
    this.dialogError = '';
    this.dialog = 'delete-chat';
  }

  addProject(event: MouseEvent): void {
    event.stopPropagation();
    this.openMenuId = null;
    this.dialogConversation = null;
    this.dialogValue = '';
    this.dialogError = '';
    this.dialog = 'create-project';
  }

  closeDialog(): void {
    if (this.dialogSaving) return;
    this.dialog = null;
    this.dialogConversation = null;
    this.dialogValue = '';
    this.dialogError = '';
  }

  async submitDialog(): Promise<void> {
    if (this.dialogSaving) return;

    const value = this.dialogValue.trim();
    if (this.dialog === 'create-project' && !value) {
      this.dialogError = 'Enter a project name.';
      return;
    }
    if (this.dialog === 'rename-chat' && !value) {
      this.dialogError = 'Enter a chat name.';
      return;
    }

    this.dialogSaving = true;
    this.dialogError = '';
    try {
      if (this.dialog === 'create-project') {
        await this.state.createGroup(value);
      } else if (this.dialog === 'rename-chat' && this.dialogConversation && value !== this.dialogConversation.title) {
        await this.state.renameConversation(this.dialogConversation, value);
      } else if (this.dialog === 'delete-chat' && this.dialogConversation) {
        await this.state.deleteConversation(this.dialogConversation);
      }
      this.dialog = null;
      this.dialogConversation = null;
      this.dialogValue = '';
    } catch {
      this.dialogError = this.dialog === 'create-project'
        ? 'Could not create the project. Please try again.'
        : this.dialog === 'rename-chat'
          ? 'Could not rename the chat. Please try again.'
          : 'Could not delete the chat. Please try again.';
    } finally {
      this.dialogSaving = false;
    }
  }

  onScroll(event: Event): void {
    const element = event.currentTarget as HTMLElement;
    if (element.scrollHeight - element.scrollTop - element.clientHeight < 96) void this.state.loadMoreRecents();
  }
}
